import type { EarTrainingMode } from '../types/earTraining';
import { tonicToPitchClass } from './pitchLabel';

/** One key on the circle of fifths (no F#/Gb major or D#/Eb minor). */
export type EarTrainingKeySpec = {
  tonic: string;
  keySignatureFifths: number;
  mode: EarTrainingMode;
};

/** Pitch class for F#/Gb major. */
export const EXCLUDED_MAJOR_TONIC_PC = 6;

/** Pitch class for D#/Eb minor (relative to F#/Gb). */
export const EXCLUDED_MINOR_TONIC_PC = 3;

/** Ten major keys on the circle, excluding F#/Gb. */
export const EAR_TRAINING_MAJOR_KEYS: readonly EarTrainingKeySpec[] = [
  { tonic: 'C', keySignatureFifths: 0, mode: 'major' },
  { tonic: 'G', keySignatureFifths: 1, mode: 'major' },
  { tonic: 'D', keySignatureFifths: 2, mode: 'major' },
  { tonic: 'A', keySignatureFifths: 3, mode: 'major' },
  { tonic: 'E', keySignatureFifths: 4, mode: 'major' },
  { tonic: 'B', keySignatureFifths: 5, mode: 'major' },
  { tonic: 'F', keySignatureFifths: -1, mode: 'major' },
  { tonic: 'Bb', keySignatureFifths: -2, mode: 'major' },
  { tonic: 'Eb', keySignatureFifths: -3, mode: 'major' },
  { tonic: 'Ab', keySignatureFifths: -4, mode: 'major' },
] as const;

/** Ten minor keys (la-based), excluding D#/Eb. */
export const EAR_TRAINING_MINOR_KEYS: readonly EarTrainingKeySpec[] = [
  { tonic: 'A', keySignatureFifths: 0, mode: 'minor' },
  { tonic: 'E', keySignatureFifths: 1, mode: 'minor' },
  { tonic: 'B', keySignatureFifths: 2, mode: 'minor' },
  { tonic: 'F#', keySignatureFifths: 3, mode: 'minor' },
  { tonic: 'C#', keySignatureFifths: 4, mode: 'minor' },
  { tonic: 'G#', keySignatureFifths: 5, mode: 'minor' },
  { tonic: 'D', keySignatureFifths: -1, mode: 'minor' },
  { tonic: 'G', keySignatureFifths: -2, mode: 'minor' },
  { tonic: 'C', keySignatureFifths: -3, mode: 'minor' },
  { tonic: 'F', keySignatureFifths: -4, mode: 'minor' },
] as const;

export function earTrainingKeysForMode(mode: EarTrainingMode): readonly EarTrainingKeySpec[] {
  return mode === 'major' ? EAR_TRAINING_MAJOR_KEYS : EAR_TRAINING_MINOR_KEYS;
}

export function isExcludedEarTrainingKey(tonic: string, mode: EarTrainingMode): boolean {
  const pc = tonicToPitchClass(tonic);
  if (mode === 'major') return pc === EXCLUDED_MAJOR_TONIC_PC;
  return pc === EXCLUDED_MINOR_TONIC_PC;
}

export function isAllowedEarTrainingKey(tonic: string, mode: EarTrainingMode): boolean {
  if (isExcludedEarTrainingKey(tonic, mode)) return false;
  return earTrainingKeysForMode(mode).some(k => k.tonic === tonic);
}

/** Shortest semitone shift to move one tonic pitch class to another (-6…+6). */
export function semitonesBetweenTonics(fromTonic: string, toTonic: string): number {
  const from = tonicToPitchClass(fromTonic);
  const to = tonicToPitchClass(toTonic);
  let delta = to - from;
  while (delta > 6) delta -= 12;
  while (delta < -6) delta += 12;
  return delta;
}

export function pickRandomEarTrainingKey(
  mode: EarTrainingMode,
  random = Math.random
): EarTrainingKeySpec {
  const list = earTrainingKeysForMode(mode);
  return list[Math.floor(random() * list.length)]!;
}
